import ProposalLink from "@client/components/utils/ProposalLink";
import { PillTab, PillTabs } from "@client/components/utils/Tabs";
import { formatMoney } from "@client/utils/formatMoney";
import { RecentIndicator } from "@client/utils/recentProposals";
import { Node } from "@engine/round/aggregate";
import { SProcessOutput } from "@lib/algoInterface/runSProcessOnRound";
import { assertUnreachable } from "@lib/utils/assertUnreachable";
import { assert } from "@lib/utils/utils";
import Matrix from "algorithm/matrix";
import clsx from "clsx";
import { useEffect, useState } from "react";

type SortDirection = "asc" | "desc";

type AllocationFlowType =
  | "funders/recommenders"
  | "recommenders/proposals"
  | "funders/proposals";
type AllocationTypeType = "accountables" | "guarantees";

function sortIndicator(sortDirection: SortDirection) {
  return sortDirection === "asc" ? "▲" : "▼";
}

function AllocationTable({
  data,
  allocationFlow,
  allocationType,
}: {
  data: AllocationsTableData;
  allocationFlow: AllocationFlowType;
  allocationType: AllocationTypeType;
}) {
  const [evaluatorSort, setEvaluatorSort] = useState<{
    evaluatorId: string;
    sortDirection: SortDirection;
  } | null>(null);
  const [evaluableSort, setEvaluableSort] = useState<{
    evaluableId: string;
    sortDirection: SortDirection;
  } | null>(null);
  const [totalSort, setTotalSort] = useState<{
    type: "column" | "row";
    sortDirection: SortDirection;
  } | null>(null);

  useEffect(() => {
    setEvaluatorSort(null);
    setEvaluableSort(null);
    setTotalSort(null);
  }, [allocationFlow, allocationType]);

  function sortByEvaluator(evaluatorId: string) {
    setEvaluableSort(null);
    setTotalSort(null);
    setEvaluatorSort((prev) => {
      const sameEvaluator = prev?.evaluatorId === evaluatorId;
      if (sameEvaluator && prev.sortDirection === "asc") {
        return null;
      }
      return {
        evaluatorId,
        sortDirection: sameEvaluator ? "asc" : "desc",
      };
    });
  }

  function sortByEvaluable(evaluableId: string) {
    setEvaluatorSort(null);
    setTotalSort(null);
    setEvaluableSort((prev) => {
      const sameEvaluable = prev?.evaluableId === evaluableId;
      if (sameEvaluable && prev.sortDirection === "asc") {
        return null;
      }
      return {
        evaluableId,
        sortDirection: sameEvaluable ? "asc" : "desc",
      };
    });
  }

  function sortByTotal(type: "column" | "row") {
    setEvaluatorSort(null);
    setEvaluableSort(null);
    setTotalSort((prev) => {
      const sameType = prev?.type === type;
      if (sameType && prev.sortDirection === "asc") {
        return null;
      }
      return {
        type,
        sortDirection: sameType ? "asc" : "desc",
      };
    });
  }

  const orderedRows = data.rows.slice();
  if (evaluatorSort) {
    const { evaluatorId, sortDirection } = evaluatorSort;
    orderedRows.sort((a, b) =>
      sortDirection === "asc"
        ? a.allocations[evaluatorId] - b.allocations[evaluatorId]
        : b.allocations[evaluatorId] - a.allocations[evaluatorId],
    );
  } else if (totalSort && totalSort.type === "row") {
    orderedRows.sort((a, b) =>
      totalSort.sortDirection === "asc"
        ? a.evaluableTotal - b.evaluableTotal
        : b.evaluableTotal - a.evaluableTotal,
    );
  }

  const orderedColumns = data.evaluators.slice();
  if (evaluableSort) {
    const { evaluableId, sortDirection } = evaluableSort;
    const { allocations } = data.rows.find(
      (e) => e.evaluable.id === evaluableId,
    )!;
    orderedColumns.sort((a, b) =>
      sortDirection === "asc"
        ? allocations[a.id] - allocations[b.id]
        : allocations[b.id] - allocations[a.id],
    );
  } else if (totalSort && totalSort.type === "column") {
    orderedColumns.sort((a, b) =>
      totalSort.sortDirection === "asc"
        ? data.evaluatorTotals[a.id] - data.evaluatorTotals[b.id]
        : data.evaluatorTotals[b.id] - data.evaluatorTotals[a.id],
    );
  }

  const cellStyle =
    "p-2 text-xs text-right bg-stone-200 dark:bg-stone-700 border-2 border-stone-100 dark:border-[#181818]";

  const topCellStyle =
    " font-bold bg-stone-300 dark:bg-stone-800! border-t-0! min-w-[70px]";

  const leftCellStyle =
    " font-bold text-stone-700 dark:text-stone-300 text-left! bg-stone-300 dark:bg-stone-800! sticky left-0 border-l-0!";

  return (
    <div className="flex flex-col overflow-y-hidden overflow-x-hidden pl-6">
      <div className="overflow-x-auto flex-1 w-full">
        <table className="relative border-collapse">
          <thead className="sticky top-0 z-10 bg-stone-100 dark:bg-[#181818]">
            <tr className="text-stone-700 dark:text-stone-300 text-sm">
              <th className="sticky top-0 left-0 bg-stone-100 dark:bg-[#181818]"></th>
              <th className={cellStyle + topCellStyle}>
                <button onClick={() => sortByTotal("row")}>
                  <span
                    className={clsx(totalSort?.type !== "row" && "invisible")}
                  >
                    {sortIndicator(totalSort?.sortDirection ?? "asc")}{" "}
                  </span>
                  Total
                </button>
              </th>
              {orderedColumns.map((evaluator) => (
                <th key={evaluator.id} className={cellStyle + topCellStyle}>
                  <button onClick={() => sortByEvaluator(evaluator.id)}>
                    <span
                      className={clsx(
                        evaluatorSort?.evaluatorId !== evaluator.id &&
                          "invisible",
                      )}
                    >
                      {sortIndicator(
                        evaluatorSort?.sortDirection ?? "asc",
                      )}{" "}
                    </span>
                    {evaluator.shortname}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className={cellStyle + leftCellStyle}>
                <button onClick={() => sortByTotal("column")}>
                  Total
                  <span
                    className={clsx(
                      "inline-block -rotate-90 ml-1",
                      totalSort?.type !== "column" && "invisible",
                    )}
                  >
                    {sortIndicator(totalSort?.sortDirection ?? "asc")}
                  </span>
                </button>
              </td>
              <td className={cellStyle}>
                {formatMoney(
                  data.rows.reduce((sum, row) => sum + row.evaluableTotal, 0),
                )}
              </td>
              {orderedColumns.map((evaluator) => (
                <td key={evaluator.id} className={cellStyle}>
                  {formatMoney(data.evaluatorTotals[evaluator.id])}
                </td>
              ))}
            </tr>
            {orderedRows.map((row) => {
              return (
                <tr key={row.evaluable.id}>
                  <td className={cellStyle + leftCellStyle}>
                    <button onClick={() => sortByEvaluable(row.evaluable.id)}>
                      {row.evaluable.type === "proposal" ? (
                        <ProposalLink proposal={row.evaluable}>
                          {row.evaluable.shortname}
                          <RecentIndicator proposalId={row.evaluable.id} />
                        </ProposalLink>
                      ) : (
                        row.evaluable.shortname
                      )}
                      <span
                        className={clsx(
                          "inline-block -rotate-90",
                          evaluableSort?.evaluableId !== row.evaluable.id &&
                            "invisible",
                        )}
                      >
                        {sortIndicator(evaluableSort?.sortDirection ?? "asc")}
                      </span>
                    </button>
                  </td>
                  <td key={row.evaluable.id + "-total"} className={cellStyle}>
                    {formatMoney(row.evaluableTotal)}
                  </td>
                  {orderedColumns.map((evaluator) => {
                    return (
                      <td key={evaluator.id} className={cellStyle}>
                        {formatMoney(row.allocations[evaluator.id])}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export type AllocationsTableData = {
  rows: AllocationTableRow[];
  evaluators: Node[];
  evaluatorTotals: { [evaluatorId: string]: number };
};

export type AllocationTableRow = {
  evaluable: Node;
  evaluableTotal: number;
  allocations: { [evaluatorId: string]: number };
};

export function getAllocationTableData(
  results: SProcessOutput,
  allocationType: "accountables" | "guarantees",
  allocationFlow:
    | "funders/recommenders"
    | "recommenders/proposals"
    | "funders/proposals",
): AllocationsTableData {
  if (allocationFlow === "funders/proposals") {
    throw new Error("Not implemented");
  }

  let layerEvaluables: Node[];
  let layerEvaluators: Node[];
  let allocationsLayerIndex: 0 | 1;
  switch (allocationFlow) {
    case "funders/recommenders":
      allocationsLayerIndex = 0;
      layerEvaluables = results.layerNodes[0];
      layerEvaluators = results.parentNodes;
      break;
    case "recommenders/proposals":
      allocationsLayerIndex = 1;
      layerEvaluables = results.layerNodes[1];
      // skip first value to avoid hold
      layerEvaluators = results.layerNodes[0].slice(1);
      break;
    default:
      assertUnreachable(allocationFlow);
  }

  const layerAllocations: Matrix<"evaluator", "evaluable"> =
    allocationType === "accountables"
      ? allocationsLayerIndex === 0
        ? results.layerAccounts[allocationsLayerIndex].rename(
            "funder -> evaluator",
            "recommender+hold -> evaluable",
          )
        : results.layerAccounts[allocationsLayerIndex].rename(
            "recommender+hold -> evaluator",
            "proposal+hold -> evaluable",
          )
      : allocationsLayerIndex === 0
        ? results.layerGuarantees[allocationsLayerIndex].rename(
            "funder -> evaluator",
            "recommender+hold -> evaluable",
          )
        : results.layerGuarantees[allocationsLayerIndex].rename(
            "recommender+hold -> evaluator",
            "proposal+hold -> evaluable",
          );

  const numEvaluables = layerEvaluables.length;
  const numEvaluators = layerEvaluators.length;
  assert(layerAllocations.n_rows === numEvaluators);
  assert(layerAllocations.n_cols === numEvaluables);

  const evaluatorTotals: { [evaluatorId: string]: number } = {};
  for (const evaluator of layerEvaluators) {
    evaluatorTotals[evaluator.id] = 0;
  }

  const rows: AllocationTableRow[] = [];
  for (let i = 0; i < numEvaluables; i++) {
    const evaluable = layerEvaluables[i];

    const allocations = {};
    let evaluableTotal = 0;
    for (let j = 0; j < layerEvaluators.length; j++) {
      const evaluator = layerEvaluators[j];
      const allocation = layerAllocations.get(j, i); // Note the transposed indices (since by convention we store evaluators as rows and evaluables as columns)

      allocations[evaluator.id] = allocation;
      evaluableTotal += allocation;
      evaluatorTotals[evaluator.id] += allocation;
    }

    rows.push({ evaluable, allocations, evaluableTotal });
  }

  rows.sort((a, b) =>
    a.evaluable.shortname.localeCompare(b.evaluable.shortname),
  );

  return { rows, evaluators: layerEvaluators, evaluatorTotals };
}

export function AllocationsView({
  results,
  initialAllocationFlow,
  initialAllocationType,
  disableViewSwitching = false,
}: {
  results: SProcessOutput;
  initialAllocationFlow?: AllocationFlowType;
  initialAllocationType?: AllocationTypeType;
  disableViewSwitching?: boolean;
}) {
  const [allocationFlow, setAllocationFlow] = useState<AllocationFlowType>(
    initialAllocationFlow || "funders/recommenders",
  );

  const [allocationType, setAllocationType] = useState<AllocationTypeType>(
    initialAllocationType || "accountables",
  );

  const [data, setData] = useState<AllocationsTableData>(
    getAllocationTableData(results, allocationType, allocationFlow),
  );

  useEffect(() => {
    setData(getAllocationTableData(results, allocationType, allocationFlow));
    // TODO(joel): We're warned that results is not used here. Jason strongly warned me against messing with dependency lists so I'm leaving it for now but we should perhaps investigate.
  }, [allocationFlow, allocationType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {!disableViewSwitching && (
        <>
          <PillTabs>
            <PillTab
              onClick={() => setAllocationType("accountables")}
              selected={allocationType === "accountables"}
            >
              Accountables
            </PillTab>
            <PillTab
              onClick={() => setAllocationType("guarantees")}
              selected={allocationType === "guarantees"}
            >
              Guarantees
            </PillTab>
          </PillTabs>
          <PillTabs>
            <PillTab
              onClick={() => setAllocationFlow("funders/recommenders")}
              selected={allocationFlow === "funders/recommenders"}
            >
              Funders / Recommenders
            </PillTab>
            <PillTab
              onClick={() => setAllocationFlow("recommenders/proposals")}
              selected={allocationFlow === "recommenders/proposals"}
            >
              Recommenders / Proposals
            </PillTab>
            {/* <PillTab
              onClick={() => setView("funders/proposals")}
              selected={view === "funders/proposals"}
            >
              Funders / Proposals
            </PillTab> */}
          </PillTabs>
        </>
      )}
      <AllocationTable
        data={data}
        allocationFlow={allocationFlow}
        allocationType={allocationType}
      />
    </>
  );
}
